import { type TRectangle, type TXYPoint, type TRectCoords, type TPolygonCoords } from '@/types';

export const DEG_TO_RAD = Math.PI / 180;

export const RAD_TO_DEG = 180 / Math.PI;

/** Interpolates between a and b (0% is a, 100% is b) */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Interpolates a point between a and b (0% is a, 100% is b) */
export function lerpPoint(a: TXYPoint, b: TXYPoint, t: number): TXYPoint {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t)];
}

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

/** Clamps an angle to between 0 (inclusive) and 360 (exclusive) */
export function clampAngleDegrees(value: number): number {
  return ((value % 360) + 360) % 360;
}

export function clampRotation(value: number, max: number, offset: number) {
  return ((clampAngleDegrees((value * 360) / max + offset) - offset) * max) / 360;
}

/** Rotates a point counter-clockwise around an origin point. Angle is in degrees. */
export function rotatePointAroundOrigin(p: TXYPoint, origin: TXYPoint, angle: number): TXYPoint {
  const [px, py] = p;
  const [ox, oy] = origin;
  const radians = (angle * Math.PI) / 180;
  const x = ox + (px - ox) * Math.cos(radians) - (py - oy) * Math.sin(radians);
  const y = oy + (px - ox) * Math.sin(radians) + (py - oy) * Math.cos(radians);
  return [x, y];
}

export const roundFloat = (num: number, decimals = 2): number => {
  const res = Math.round(num * 10 ** decimals) / 10 ** decimals;
  if (Object.is(res, -0)) return 0;
  return res;
};

export const roundPoint = ([x, y]: TXYPoint, decimals = 2): TXYPoint => [
  roundFloat(x, decimals),
  roundFloat(y, decimals),
];

export const sumPoints = (...points: TXYPoint[]): TXYPoint =>
  points.reduce((acc, [x, y]) => [acc[0] + x, acc[1] + y], [0, 0]);

export const scalePoint = (p: TXYPoint, scale: number): TXYPoint =>
  p.map((val) => val * scale) as TXYPoint;

export const sumRectangles = (rect1: TRectangle, rect2: TRectangle): TRectangle =>
  rect1.map((val, i) => val + rect2[i]) as TRectangle;

// Accepts the origin and target points, and the zoom level.
// Returns the new origin point.
export function zoomToPoint(
  origin: TXYPoint,
  target: TXYPoint,
  prevZoom: number,
  nextZoom: number,
): TXYPoint {
  const [x, y] = origin;
  const [tx, ty] = target;
  const zoomRatio = prevZoom / nextZoom;
  return [tx + (x - tx) * zoomRatio, ty + (y - ty) * zoomRatio];
}

/** Converts an array of points into a rectangle that contains all of them. */
export function pointsToRect(...points: TXYPoint[]): TRectangle {
  const xpts = points.map(([x]) => x);
  const ypts = points.map(([, y]) => y);
  const x = Math.min(...xpts);
  const y = Math.min(...ypts);
  // Decimal operation causes inaccuracy in javascript, it's recommended to multiply the value to integer before doing subtraction or addition
  const width = (Math.max(...xpts) * 100 - x * 100) / 100;
  const height = (Math.max(...ypts) * 100 - y * 100) / 100;
  return [x, y, width, height];
}

/** Converts a rectangle (x,y,width,height) into a set of coordinates for each corner. */
export function rectToPoints(x: number, y: number, width: number, height: number): TRectCoords {
  return [
    [x, y],
    [x + width, y],
    [x + width, y + height],
    [x, y + height],
  ];
}

/** Calculates the inclination degree between a line and x-axis, the value is from 0 to 360 degrees.  */
export const calculateInclination = (x1: number, y1: number, x2: number, y2: number) => {
  const deltaX = x2 - x1;
  const deltaY = y2 - y1;
  return clampAngleDegrees(Math.atan2(deltaY, deltaX) * RAD_TO_DEG + 180);
};

/**
 * Assumes that points are in counter-clockwise order,
 * starting from the bottom-left point for 0° rotation,
 * bottom-right for 90° rotation, etc.
 */
export function getRectRotation(points: TRectCoords) {
  // Left bottom corner
  const [x1, y1] = points[0];
  // Right bottom corner
  const [x2, y2] = points[1];
  const rotation =
    x1 === x2
      ? y1 < y2
        ? 90
        : 270
      : y1 === y2
      ? x1 < x2
        ? 0
        : 180
      : (x1 > x2 ? 180 : 0) + Math.atan((y1 - y2) / (x1 - x2)) * RAD_TO_DEG;
  return clampAngleDegrees(parseInt(rotation.toFixed(0)));
}

/**
 * Safely creates a TXYPoint from two numbers
 */
export function createPoint(x: number, y: number): TXYPoint {
  return [x, y];
}

/**
 * Type guard to check if coordinates represent a rectangle (4 points)
 */
export function isRectCoords(coords: TRectCoords | TPolygonCoords): coords is TRectCoords {
  return coords.length === 4;
}

/**
 * Type guard to check if coordinates represent a polygon (any number of points)
 */
export function isPolygonCoords(coords: TRectCoords | TPolygonCoords): coords is TPolygonCoords {
  return Array.isArray(coords) && coords.length >= 3;
}

/**
 * Converts any coordinate array to TPolygonCoords
 */
export function toPolygonCoords(coords: TRectCoords | TPolygonCoords): TPolygonCoords {
  return coords;
}

/**
 * Ensures coordinates are valid TPolygonCoords
 */
export function ensurePolygonCoords(coords: TXYPoint[]): TPolygonCoords {
  if (coords.length < 3) {
    throw new Error('Polygon must have at least 3 points');
  }
  return coords;
}
